import { useRef } from 'react';
import { isDev } from 'tradera/legacy/static/utils/environment';
import { GrowthBook } from '@growthbook/growthbook-react';
import { GtmService } from 'tradera/services/google-tagmanager/google-tagmanager-service';
import { useOnMount } from './use-on-mount';
import { useAppSelector } from 'tradera/state/hooks';
import { selectMemberId } from 'tradera/state/member/selectors';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { selectShippingRegionCountryCodeIso2 } from 'tradera/state/shipping-region/selectors';

const GROWTHBOOK_API_HOST = 'https://growthbook-proxy.tradera.cloud';

export const useTWGrowthBook = () => {
    const clientKey = useAppSelector(
        (state) => state.initData.growthBookClientKey
    );
    const memberId = useAppSelector(selectMemberId);
    const isGrowthBookEnabled = useIsFeatureEnabled('growth-book');
    const memberCountryCodeIso2 = useAppSelector(
        selectShippingRegionCountryCodeIso2
    );

    const growthBookRef = useRef<GrowthBook<Record<string, unknown>> | null>(
        null
    );

    // Force enable GrowthBook feature with query param ?force-gb
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    const forceEnable = params.hasOwnProperty('force-gb');
    const forceVariant = params['force-gb']?.toString() ?? null;

    // https://docs.growthbook.io/guide/GA4-google-analytics#using-gas-client-id
    const gaId = isGrowthBookEnabled
        ? document?.cookie?.match(/_ga=(.+?);/)
        : null;

    let clientId: string | null = null;

    if (gaId !== null && gaId.length > 1) {
        const idParts = gaId[1].split('.');
        if (idParts.length >= 2) {
            clientId = idParts.slice(-2).join('.');
        }
    }

    // Conditionally create a GrowthBook instance
    if (isGrowthBookEnabled && growthBookRef.current === null) {
        growthBookRef.current = new GrowthBook({
            clientKey,
            apiHost: GROWTHBOOK_API_HOST,
            enableDevMode: isDev(),
            backgroundSync: false,
            trackingCallback: (experiment, result) => {
                GtmService.trackGtmEvent('experiment_viewed', {
                    event_category: 'experiment',
                    experiment_id: experiment.key,
                    variation_id: result.variationId
                });
            }
        });
        growthBookRef.current?.setAttributes({
            clientId: clientId ?? null,
            deviceId: clientId ?? null,
            memberId: memberId ?? null,
            country: memberCountryCodeIso2 ?? null,
            loggedIn: !!memberId,
            forceEnable,
            forceVariant
        });
        window._growthbook = growthBookRef.current;
    }

    //Setup Growthbook
    useOnMount(() => {
        // Load features from the GrowthBook API
        if (isGrowthBookEnabled) {
            growthBookRef.current?.loadFeatures({
                timeout: 2000
            });
        }
    });

    return isGrowthBookEnabled ? growthBookRef.current : null;
};
